import type { SessionUser } from '@accounts/shared';
import { useQuery } from '@tanstack/react-query';

import { getAccessQueue } from '../../../lib/api';
import { useBellQuery } from '../../../lib/notifications';
import { accessQueueScopeOf, queryKeys } from '../../../lib/queryKeys';
import { canEnter, surfaceById } from '../../../lib/surfaces';

/**
 * The Access counts the CHROME shows — the rail entry's red badge and the hub sub-nav's
 * Queue count. Same key as `useRoleQueue` (the server scopes HR's read), so the cache is shared and the
 * badge, the sub-nav and the Queue's own lanes can never disagree. Separate from it only
 * because the chrome renders for a signed-out moment too (`user` may be null) and for
 * people who cannot enter the hub at all, for whom nothing is read.
 *
 * THE RED COUNT IS THE SERVER'S (spec row 14): `accessRequestCount` on the bell read — every
 * pending catalog and Other request for an administrator, only pending catalog requests on
 * "HR can grant directly" rows for HR. Other requests are not Queue items (they have their
 * own read), so a count taken from the Queue alone would leave them out.
 */
export interface AccessCounts {
  /** Pending requests the actor can act on, Other requests included — the red count. */
  waitingRequests: number;
  /** Every Queue item the actor can act on — the sub-nav count. */
  waiting: number;
  anyOverdue: boolean;
  /** False until the reads have landed; the chrome shows no number before then. */
  ready: boolean;
}

export function useAccessCounts(user: SessionUser | null): AccessCounts {
  const enabled = canEnter(user, surfaceById('access').capability);

  const queue = useQuery({
    // `user` is non-null whenever `enabled` is; the fallback only names a key nothing reads.
    queryKey: queryKeys.accessQueue(user === null ? 'limited' : accessQueueScopeOf(user), {}),
    queryFn: () => getAccessQueue(),
    enabled,
    // Light polling, the same cadence as the bell: new requests arrive without a reload.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  // Shares the bell's cache entry, so the rail, the sub-nav and the bell read one answer.
  const bell = useBellQuery(enabled ? user : null);

  const items = queue.data?.data ?? [];
  const deactivations = queue.data?.deactivations ?? [];

  return {
    waitingRequests: enabled ? (bell.data?.meta.accessRequestCount ?? 0) : 0,
    waiting: items.length + deactivations.length,
    anyOverdue: items.some((item) => item.overdue) || deactivations.some((item) => item.overdue),
    ready: queue.data !== undefined,
  };
}
